/**
 * SAFUAYE — Interactive map of the Atchrimi-Komé headquarters (Atakpamé, Togo).
 *
 * Renders a Leaflet map with a single marker on the SAFUAYE-TOGO siège,
 * with a rich popup showing address, key facts and thumbnails from the
 * September 12, 2026 official meeting. Aimed at UK Trustees and donors
 * who have never been to Togo, to give a tangible sense of the place.
 */
import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icons under bundlers (Webpack/CRA) which strip URLs.
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// Atchrimi-Komé neighbourhood in Atakpamé, Ogou 1 · Togo.
// Approximate GPS — to refine when a precise reading is available on-site.
const SIEGE_POSITION = [7.5326, 1.1276];

const PHOTO_PREVIEW = [
  "IMG-20260912-WA0008.jpg",
  "IMG-20260912-WA0011.jpg",
  "IMG-20260912-WA0013.jpg",
  "IMG-20260912-WA0016.jpg",
];

const T = {
  fr: {
    kicker: "Trouvez-nous sur la carte",
    title: "Le siège SAFUAYE-TOGO à Atakpamé",
    sub: "Quartier Atchrimi-Komé · Commune d'Ogou 1 · Togo. Cliquez le repère pour voir les détails et l'aperçu photo.",
    popupName: "Siège SAFUAYE-TOGO",
    popupAddr: "Atchrimi-Komé · Atakpamé · Ogou 1 · Togo",
    popupFact1: "Bâtiment familial rénové (2026)",
    popupFact2: "Accueille l'AG et les rencontres bénéficiaires",
    popupCta: "Album 12 sept. 2026",
    tooltip: "SAFUAYE-TOGO · Atchrimi-Komé",
    disclaimer: "GPS indicatif · à préciser après relevé sur place.",
    osm: "Ouvrir dans OpenStreetMap",
    directions: "Itinéraire",
    gmaps: "Google Maps",
    waze: "Waze",
    fromLome: "Depuis l'aéroport de Lomé : ~166 km · ~3 h de route",
  },
  en: {
    kicker: "Find us on the map",
    title: "SAFUAYE-TOGO headquarters in Atakpamé",
    sub: "Atchrimi-Komé neighbourhood · Ogou 1 commune · Togo. Click the pin for details and a photo preview.",
    popupName: "SAFUAYE-TOGO headquarters",
    popupAddr: "Atchrimi-Komé · Atakpamé · Ogou 1 · Togo",
    popupFact1: "Renovated family building (2026)",
    popupFact2: "Hosts the AGM and beneficiary meetings",
    popupCta: "Album — 12 Sept 2026",
    tooltip: "SAFUAYE-TOGO · Atchrimi-Komé",
    disclaimer: "Indicative GPS — to be refined on site.",
    osm: "Open in OpenStreetMap",
    directions: "Get directions",
    gmaps: "Google Maps",
    waze: "Waze",
    fromLome: "From Lomé airport: ~166 km · ~3 h drive",
  },
  it: {
    kicker: "Trovaci sulla mappa",
    title: "La sede SAFUAYE-TOGO ad Atakpamé",
    sub: "Quartiere Atchrimi-Komé · Comune di Ogou 1 · Togo. Clicca il segnaposto per i dettagli e un'anteprima fotografica.",
    popupName: "Sede SAFUAYE-TOGO",
    popupAddr: "Atchrimi-Komé · Atakpamé · Ogou 1 · Togo",
    popupFact1: "Edificio familiare ristrutturato (2026)",
    popupFact2: "Ospita l'AG e gli incontri con i beneficiari",
    popupCta: "Album — 12 sett. 2026",
    tooltip: "SAFUAYE-TOGO · Atchrimi-Komé",
    disclaimer: "GPS indicativo — da precisare in loco.",
    osm: "Apri in OpenStreetMap",
    directions: "Itinerario",
    gmaps: "Google Maps",
    waze: "Waze",
    fromLome: "Dall'aeroporto di Lomé: ~166 km · ~3 h di viaggio",
  },
};

export function SiegeMapSection({ lang = "fr", apiBase = "" }) {
  // Patch Leaflet's default icon URLs once per mount.
  useEffect(() => {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: markerIcon2x,
      iconUrl: markerIcon,
      shadowUrl: markerShadow,
    });
  }, []);

  const t = T[lang] || T.fr;
  const [lat, lon] = SIEGE_POSITION;

  return (
    <section
      id="siege-map"
      data-testid="siege-map-section"
      className="py-16 md:py-20 bg-gradient-to-b from-white to-emerald-50/50"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-8">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-2">
            📍 {t.kicker}
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            {t.title}
          </h2>
          <p className="text-sm md:text-base text-gray-600 max-w-3xl mx-auto">
            {t.sub}
          </p>
        </div>

        <div
          className="relative rounded-3xl overflow-hidden shadow-2xl border-2 border-emerald-200"
          data-testid="siege-map-container"
        >
          <MapContainer
            center={SIEGE_POSITION}
            zoom={15}
            scrollWheelZoom={false}
            style={{ height: "480px", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={SIEGE_POSITION}>
              <Tooltip direction="top" offset={[0, -30]} opacity={0.95} permanent>
                <span className="text-xs font-semibold text-emerald-800">
                  {t.tooltip}
                </span>
              </Tooltip>
              <Popup maxWidth={340} minWidth={260}>
                <div className="p-1 font-sans" data-testid="siege-map-popup">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-orange-600 mb-1">
                    📍 SAFUAYE-TOGO
                  </p>
                  <p className="text-base font-bold text-gray-900 leading-tight mb-1">
                    {t.popupName}
                  </p>
                  <p className="text-xs text-gray-600 leading-snug mb-2">
                    {t.popupAddr}
                  </p>

                  <ul className="text-xs text-gray-700 space-y-1 mb-3 border-y border-gray-200 py-2">
                    <li className="flex items-start gap-1.5">
                      <span className="text-emerald-600 font-bold">✓</span>
                      <span>{t.popupFact1}</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <span className="text-emerald-600 font-bold">✓</span>
                      <span>{t.popupFact2}</span>
                    </li>
                  </ul>

                  <div className="grid grid-cols-4 gap-1 mb-3">
                    {PHOTO_PREVIEW.map((f, i) => (
                      <a
                        key={i}
                        href={`${apiBase}/media/${f}`}
                        target="_blank"
                        rel="noreferrer"
                        className="relative rounded overflow-hidden aspect-square block bg-gray-100"
                        data-testid={`siege-map-thumb-${i}`}
                      >
                        <img
                          src={`${apiBase}/media/${f}`}
                          alt={`Rencontre 12/09/2026 · ${i + 1}`}
                          className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                          loading="lazy"
                        />
                      </a>
                    ))}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <a
                      href={`${apiBase}/docs/SAFUAYE_Album_Rencontre_12sept2026.pdf`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-1 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md transition-colors"
                      data-testid="siege-map-cta-album"
                    >
                      📸 {t.popupCta}
                    </a>

                    {/* Directions block — Google Maps + Waze */}
                    <div className="pt-1.5 mt-0.5 border-t border-gray-200">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                        🧭 {t.directions}
                      </p>
                      <div className="grid grid-cols-2 gap-1.5">
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}&destination_place_id=SAFUAYE-TOGO+Atchrimi-Kome+Atakpame`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center gap-1 text-[11px] font-semibold bg-blue-600 hover:bg-blue-700 text-white px-2 py-1.5 rounded-md transition-colors"
                          data-testid="siege-map-cta-gmaps"
                        >
                          <span aria-hidden="true">🗺️</span> {t.gmaps}
                        </a>
                        <a
                          href={`https://www.waze.com/ul?ll=${lat}%2C${lon}&navigate=yes&zoom=15`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center gap-1 text-[11px] font-semibold bg-cyan-500 hover:bg-cyan-600 text-white px-2 py-1.5 rounded-md transition-colors"
                          data-testid="siege-map-cta-waze"
                        >
                          <span aria-hidden="true">🚗</span> {t.waze}
                        </a>
                      </div>
                      <p className="text-[10px] text-gray-500 italic mt-1.5 leading-snug">
                        ✈️ {t.fromLome}
                      </p>
                    </div>

                    <a
                      href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=17/${lat}/${lon}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-800 hover:underline mt-1"
                    >
                      ↗ {t.osm}
                    </a>
                  </div>
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        </div>

        <p
          className="text-xs text-gray-500 italic text-center mt-3"
          data-testid="siege-map-disclaimer"
        >
          {t.disclaimer}
        </p>
      </div>
    </section>
  );
}

export default SiegeMapSection;
